/**
 * Payment Method Types
 *
 * Defines the interface for payment method plugins.
 * Each payment method (card, Apple Pay, V12, etc.) implements this interface.
 */

import type { MarketContext } from '../markets/types';

/**
 * All supported payment types
 */
export const PAYMENT_TYPES = {
  // Card Payments
  CARD: 'card',
  VISA: 'visa',
  SCHEME: 'scheme',

  // Digital Wallets
  APPLE_PAY: 'applepay',
  APPLE_PAY_EXPRESS: 'applepay_express',
  GOOGLE_PAY: 'googlepay',
  PAY_WITH_GOOGLE: 'paywithgoogle',
  UNION_PAY: 'unionpay',
  ALIPAY: 'alipay',

  // PayPal
  PAYPAL: 'paypal',
  PAYPAL_EXPRESS: 'paypal_express',

  // Buy Now Pay Later
  KLARNA_PAYNOW: 'klarna_paynow',
  AFFIRM: 'affirm',
  ZIP: 'zip',

  // European Local Methods
  IDEAL: 'ideal',
  TWINT: 'twint',
  BANCONTACT: 'bcmc',
  FACILYPAY_3X: 'facilypay_3x',
  FACILYPAY_4X: 'facilypay_4x',
  PAYBYBANK: 'paybybank',
  PAYBYBANK_US: 'paybybank_AIS_DD',

  // Financing
  V12: 'v12',
  CEMBRA: 'cembra_installment',
  CEMBRA_INVOICE: 'cembra_invoice',
  CREDIT_PLUS: 'creditplus',
  AGOS: 'agos',
  ORICO_FINANCING: 'orico_financing',
  SMBC: 'smbc',

  // Other
  OFFLINE: 'offline_banktransfer',
} as const;

export type PaymentType = (typeof PAYMENT_TYPES)[keyof typeof PAYMENT_TYPES] | string;

// Re-export split payment types from the dedicated module
export type { PaymentRecord, SplitPaymentConfig } from './split';

/**
 * Payment method categories
 */
export type PaymentMethodCategory =
  | 'card' // Credit/debit cards
  | 'wallet' // Apple Pay, Google Pay
  | 'redirect' // PayPal, iDEAL
  | 'bnpl' // Buy now pay later (Klarna, Affirm)
  | 'financing' // V12, Cembra, etc.
  | 'bank' // Bank transfer
  | 'other';

/**
 * Payment method availability check result
 */
export interface PaymentAvailability {
  available: boolean;
  reason?: string; // Why not available
  minAmount?: number; // Minimum order amount
  maxAmount?: number; // Maximum order amount
  splitPaymentAllowed: boolean;
  oncePerOrder: boolean; // Can only be used once per order
  incompatibleWith?: string[]; // Other methods that can't be used together
}

/**
 * Payment result from method processing
 */
export interface PaymentResult {
  success: boolean;
  requiresAction: boolean;
  action?: {
    type: 'redirect' | '3ds' | 'polling' | 'iframe';
    url?: string;
    data?: Record<string, unknown>;
  };
  transactionId?: string;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Payment state during processing
 */
export interface PaymentState {
  methodType: string;
  amount: number;
  currency: string;
  status: 'idle' | 'processing' | 'awaiting_action' | 'completed' | 'failed';
  transactionId?: string;
  error?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Payment method plugin interface
 * All payment methods must implement this
 */
export interface PaymentMethod {
  /**
   * Unique identifier for this payment method
   */
  type: string;

  /**
   * Display name (i18n key)
   */
  nameKey: string;

  /**
   * Category for grouping
   */
  category: PaymentMethodCategory;

  /**
   * Icon component or URL
   */
  icon?: string;

  /**
   * Check if this payment method is available
   */
  checkAvailability(context: {
    marketContext: MarketContext;
    amount: number;
    isSplitPayment: boolean;
    existingPayments: string[];
  }): PaymentAvailability;

  /**
   * Initialize the payment method (load scripts, etc.)
   */
  initialize?(context: {
    marketContext: MarketContext;
    checkoutId: string;
    amount: number;
  }): Promise<void>;

  /**
   * Render payment method UI (returns component props)
   */
  getUIProps?(context: {
    marketContext: MarketContext;
    amount: number;
    onStateChange: (state: Partial<PaymentState>) => void;
  }): Record<string, unknown>;

  /**
   * Process the payment
   */
  process(context: {
    marketContext: MarketContext;
    checkoutId: string;
    amount: number;
    paymentData: Record<string, unknown>;
  }): Promise<PaymentResult>;

  /**
   * Handle return from redirect/3DS
   */
  handleReturn?(context: {
    searchParams: URLSearchParams;
    checkoutId: string;
  }): Promise<PaymentResult>;

  /**
   * Clean up resources
   */
  cleanup?(): void;
}

/**
 * Payment method configuration from backend
 * (from Adyen dropInConfiguration or custom config)
 */
export interface PaymentMethodConfig {
  type: string;
  name: string;
  available: boolean;
  splitPayment: boolean;
  limitMin?: number;
  limitMax?: number;
  dropInConfiguration?: Record<string, unknown>;
}

/**
 * Split payment progress tracking
 */
export interface SplitPaymentProgress {
  maxPayments: number;
  minAmount: number;
  paidAmount: number;
  remainingAmount: number;
  payments: Array<{
    method: string;
    amount: number;
    transactionId?: string;
  }>;
}
